import React from 'react';
import Icon from '../../../components/AppIcon';

const OrderTrackingSection = ({ status, orderId }) => {
  const trackingStages = [
    {
      id: 'confirmed',
      label: 'Order Confirmed',
      icon: 'CheckCircle2',
      description: 'Your order has been received',
      completed: true
    },
    {
      id: 'preparing',
      label: 'Preparing',
      icon: 'ChefHat',
      description: 'Our chefs are preparing your meal',
      completed: status === 'preparing' || status === 'dispatched' || status === 'delivered',
      active: status === 'preparing'
    },
    {
      id: 'dispatched',
      label: 'Out for Delivery',
      icon: 'Truck',
      description: 'Your order is on the way',
      completed: status === 'dispatched' || status === 'delivered',
      active: status === 'dispatched'
    },
    {
      id: 'delivered',
      label: 'Delivered',
      icon: 'Package',
      description: 'Enjoy your meal!',
      completed: status === 'delivered',
      active: status === 'delivered'
    }
  ];

  return (
    <div className="bg-card rounded-xl shadow-lg p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-6 md:mb-8">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Package" size={20} color="var(--color-primary)" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-foreground">Track Your Order</h2>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">Order #{orderId}</p>
        </div>
      </div>

      <div className="relative">
        {trackingStages?.map((stage, index) => (
          <div key={stage?.id} className="relative flex gap-4 md:gap-6 pb-8 last:pb-0">
            {/* Timeline Line */}
            {index < trackingStages?.length - 1 && (
              <div className="absolute left-5 md:left-6 top-12 md:top-14 bottom-0 w-0.5 bg-border" />
            )}

            {/* Status Icon */}
            <div className="relative z-10 flex-shrink-0">
              <div
                className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all ${
                  stage?.completed
                    ? 'bg-accent text-accent-foreground'
                    : stage?.active
                    ? 'bg-primary text-primary-foreground animate-pulse-subtle'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                <Icon name={stage?.icon} size={20} />
              </div>
            </div>

            {/* Status Content */}
            <div className="flex-1 pt-1">
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">{stage?.label}</h3>
              <p className="text-xs md:text-sm text-muted-foreground">{stage?.description}</p>
              {stage?.active && (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full">
                  <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                  <span className="text-xs font-semibold text-primary">In Progress</span>
                </div>
              )}
              {stage?.completed && !stage?.active && (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-accent/10 rounded-full">
                  <Icon name="Check" size={12} color="var(--color-accent)" />
                  <span className="text-xs font-semibold text-accent">Completed</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 md:mt-8 p-3 md:p-4 bg-primary/10 rounded-lg border border-primary/30">
        <div className="flex gap-2 md:gap-3">
          <Icon name="Bell" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="text-xs md:text-sm text-foreground">
            <p className="font-semibold mb-1">Real-time Updates</p>
            <p className="text-muted-foreground">
              You'll receive WhatsApp notifications as your order progresses through each stage.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingSection;